import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useApi from "../../hooks/api/axiosInterceptor";

const Wrapper = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;
    padding: 2rem;
    
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    text-align: center;
`;

const LoginWrap = styled.div`
    width: 100%;
    max-width: 450px;
    margin: 20px 20px;
    padding: 35px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 30px;
    text-align: center;
    color: #333;
`;

const InputRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;

const InputLabel = styled.span`
    width: 100px;
    text-align: left;
    font-size: 15px;
    font-weight: 500;
    color: #555;
`;

const Input = styled.input`
    flex: 1;
    height: 40px;
    padding: 0 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        border-color: #4b70fd;
        outline: none;
    }
`;

const SearchButton = styled.button`
    width: 100%;
    height: 48px;  
    padding: 12px 0;
    margin-top: 15px;
    background-color: #2B4F95;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
        background-color: #1a3a70;
    }
    
    &:disabled {
        background-color: #b3c1d1;
        cursor: not-allowed;
    }
`;

const ResultMessage = styled.div`
    margin: 20px 0;
    font-size: 16px;
    line-height: 1.6;
    
    span.highlight {
        color: #2056ad;
        font-weight: bold;
        font-size: 18px;
        display: inline-block;
        margin: 0 3px;
    }
`;

const HelpLinks = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    font-size: 14px;
`;

const StyledLink = styled(Link)`
    color: #666;
    text-decoration: none;
    
    &:hover {
        text-decoration: underline;
        color: #0066cc;
    }
`;

const PwSearchByEmail = () => {
    const [userId, setUserId] = useState("");
    const [name, setName] = useState("");
    const [backupEmail, setBackupEmail] = useState("");
    const [searchResult, setSearchResult] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleSearch = async () => {
        if (!userId.trim()) {
            alert("아이디를 입력해주세요");
            return;
        }
        if (!name.trim()) {
            alert("이름을 입력해주세요");
            return;
        }
        if (!backupEmail.trim()) {
            alert("가입시 등록하신 예비이메일을 입력하세요");
            return;
        }
    
        setIsSearching(true);
        setErrorMsg("");
    
        try {
            // API 호출
            const response = await useApi.post("/auth/pw_search_by_email", {
                userId: userId,
                name: name,
                email: backupEmail
            });
    
            // status 필드로 성공/실패 판단
            if (response.data.status === "SUCCESS") {
                setSearchResult({
                    success: true,
                    name: response.data.data.name,
                    email: response.data.data.email
                });
            } else {
                // ERROR 상태라도 API 호출은 성공했으므로 처리
                setSearchResult({
                    success: false,
                    name: name,
                    message: response.data.message || "입력하신 정보와 맞는 로그인정보가 없습니다."
                });
            }
        } catch (error) {
            console.error("비밀번호 찾기 오류:", error);
            setErrorMsg("서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <Wrapper>
            <LoginWrap>
                <Title>예비 이메일로 비밀번호 찾기</Title>
                
                {!searchResult ? (
                    <>
                        <InputRow>
                            <InputLabel>아이디</InputLabel>
                            <Input 
                                type="text" 
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                placeholder="아이디를 입력하세요"
                            />
                        </InputRow>
                        
                        <InputRow>
                            <InputLabel>이름</InputLabel>
                            <Input 
                                type="text" 
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="이름을 입력하세요"
                                maxLength="20"
                            />
                        </InputRow>
                        
                        <InputRow>
                            <InputLabel>예비이메일</InputLabel>
                            <Input 
                                type="email" 
                                value={backupEmail}
                                onChange={(e) => setBackupEmail(e.target.value)}
                                placeholder="예비이메일을 입력하세요"
                            />
                        </InputRow>
                        
                        {errorMsg && <p style={{ color: "red", fontSize: "14px" }}>{errorMsg}</p>}
                        
                        <SearchButton 
                            onClick={handleSearch}
                            disabled={isSearching}
                        >
                            {isSearching ? "검색 중..." : "비밀번호 찾기"}
                        </SearchButton>
                        
                        <HelpLinks>
                            <StyledLink to="/intro/login">로그인</StyledLink>
                            <StyledLink to="/intro/login/id-search">아이디 찾기</StyledLink>
                            <StyledLink to="/intro/login/pw-search">비밀번호 찾기</StyledLink>
                        </HelpLinks>
                    </>
                ) : (
                    <ResultMessage>
                    {searchResult.success ? (
                        <>
                            <p><span className="highlight">{searchResult.name}</span> 회원님 반갑습니다.</p>
                            <p>회원님의 예비이메일(<span className="highlight">{searchResult.email}</span>)로 임시비밀번호가 발송되었습니다.</p>
                            <SearchButton onClick={() => window.location.href = "/intro/login"}>
                                로그인하기
                            </SearchButton>
                        </>
                    ) : (
                        <>
                            <p><span className="highlight">{searchResult.name}</span>님 께서</p>
                            <p>{searchResult.message || "입력하신 정보와 맞는 로그인정보가 없습니다."}</p>
                            <p>회원가입 후에 이용하세요.</p>
                            <SearchButton onClick={() => setSearchResult(null)}>
                                다시 검색
                            </SearchButton>
                        </>
                    )}
                </ResultMessage>
                )}
            </LoginWrap>
        </Wrapper>
    );
};

export default PwSearchByEmail;